/**
 * Typed collection helpers for `@proof/client-core`.
 *
 * Wraps the oRPC `collection.*` procedures exposed by `apps/api` with
 * convenience functions that match the client contract expected by the
 * Explorer UI (Track 2.J):
 *
 *   listCollections()
 *   createCollection(name)
 *   renameCollection(uuid, name)
 *   deleteCollection(uuid)
 *   listRecipesInCollection(collectionUuid)
 *   listCollectionsForRecipe(recipeUuid)
 *   addMembership(collectionUuid, recipeUuid)
 *   removeMembership(collectionUuid, recipeUuid)
 *
 * Slug derivation: the server requires a slug on create.  We derive one from
 * the `name` argument client-side (lowercase, spaces → hyphens, strip
 * non-alphanumeric except hyphens, collapse consecutive hyphens, trim leading/
 * trailing hyphens).  If two collections would produce the same slug the
 * server returns a `409 CONFLICT` / `slug_taken` error; the caller is
 * responsible for surfacing that to the user.
 *
 * Architecture refs:
 *   §3   — apps/api owns the oRPC router; clients consume the typed contract.
 *   §4.7 — collections DDL (flat at MVP; parent_collection_uuid always NULL).
 *   Track 2.D.3 — Collection CRUD + membership.
 *   Track 2.J   — Explorer collection overlay UI consumes this client.
 */

// ── Collection API surface ────────────────────────────────────────────────────
//
// We define the minimal callable interface that `createCollectionsClient`
// requires rather than importing `ApiClient` directly.  `ApiClient` is
// `RouterClient<AppRouter>`, and `AppRouter` currently has a pre-existing type
// error in `apps/api/src/rpc/router.ts` (oRPC context mismatch — the file is
// owned by a different track and is read-only for this chunk).  Importing
// `ApiClient` directly causes the inferred router shape to appear empty, which
// cascades type errors into every `api.collection.*` call site.
//
// The `CollectionApiSurface` interface below is structurally compatible with
// the real `ApiClient` at the `collection` namespace level.  Any `ApiClient`
// instance satisfies it at runtime because the oRPC dynamic proxy forwards
// calls regardless of TS errors in the router type.

/** Structural interface covering the `collection.*` oRPC procedures. */
export interface CollectionApiSurface {
  collection: {
    create(input: { name: string; slug: string }): Promise<unknown>;
    get(input: { uuid: string }): Promise<unknown>;
    list(input: Record<string, never> | undefined): Promise<unknown>;
    rename(input: { uuid: string; name: string; slug?: string }): Promise<unknown>;
    delete(input: { uuid: string }): Promise<unknown>;
    addMember(input: { collectionUuid: string; recipeUuid: string }): Promise<unknown>;
    removeMember(input: { collectionUuid: string; recipeUuid: string }): Promise<unknown>;
    listMembers(input: { collectionUuid: string }): Promise<unknown>;
    listRecipeMemberships(input: { recipeUuid: string }): Promise<unknown>;
    listAllMembers(input: Record<string, never> | undefined): Promise<unknown>;
  };
}

// ── Slug derivation ───────────────────────────────────────────────────────────

/**
 * Derive a URL-safe slug from a human-readable collection name.
 *
 * Rules (mirror server-side SLUG_RE):
 *   - Lowercase
 *   - Spaces → hyphens
 *   - Strip any char that is not `[a-z0-9-]`
 *   - Collapse consecutive hyphens
 *   - Trim leading/trailing hyphens
 *   - Truncate to 100 characters
 *   - If result is empty, fall back to "collection"
 */
export function slugifyCollectionName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
  return slug.length > 0 ? slug : "collection";
}

// ── Response types ────────────────────────────────────────────────────────────

/** Unwrapped collection row returned by oRPC procedures. */
export interface CollectionDto {
  uuid: string;
  slug: string;
  name: string;
  parent_collection_uuid: string | null;
  created_at: number;
}

/** Unwrapped collection-member row returned by oRPC procedures. */
export interface CollectionMemberDto {
  collection_uuid: string;
  recipe_uuid: string;
  added_at: number;
}

/**
 * Narrow membership row returned by the batched `listAllMembers` procedure
 * (perf plan §2.C). Carries only the two columns the library membership map
 * needs — `added_at` is omitted server-side.
 */
export interface CollectionMembershipDto {
  collection_uuid: string;
  recipe_uuid: string;
}

// ── Collections client factory ────────────────────────────────────────────────

/**
 * Create a typed collections API client bound to an existing `ApiClient`.
 *
 * The parameter type is `CollectionApiSurface` — a structural interface that
 * covers only the `collection.*` oRPC procedures this client needs.  Any real
 * `ApiClient` instance satisfies this interface at runtime.  The narrower type
 * avoids a cascading type error caused by the pre-existing `router.ts` oRPC
 * context mismatch (see comment above).
 *
 * The returned object exposes convenience methods that match the client
 * contract Track 2.J depends on, with names that mirror the Explorer UI's
 * call sites.
 *
 * Usage:
 *   const api = createApiClient({ baseUrl: "https://proofcook.com" });
 *   const col = createCollectionsClient(api);
 *   const { collections } = await col.listCollections();
 *   await col.createCollection("Weeknights");
 */
export function createCollectionsClient(api: CollectionApiSurface) {
  return {
    /**
     * List all collections for the authenticated user.
     * Returns `{ collections: CollectionDto[] }`.
     */
    async listCollections(): Promise<{ collections: CollectionDto[] }> {
      const result = await api.collection.list({});
      return result as { collections: CollectionDto[] };
    },

    /**
     * Create a new collection with the given display name.
     *
     * A slug is derived automatically from `name`.  If the derived slug is
     * already taken the server returns a 409 — surface as an error to the user.
     *
     * Returns the created `CollectionDto`.
     */
    async createCollection(name: string): Promise<CollectionDto> {
      const slug = slugifyCollectionName(name);
      const result = await api.collection.create({ name, slug });
      return result as CollectionDto;
    },

    /**
     * Rename a collection.  The slug is re-derived from the new name unless
     * the caller needs to preserve the existing slug — in that case use the
     * oRPC client's `collection.rename` directly with an explicit `slug`.
     *
     * Returns the updated `CollectionDto`.
     */
    async renameCollection(uuid: string, name: string): Promise<CollectionDto> {
      const slug = slugifyCollectionName(name);
      const result = await api.collection.rename({ uuid, name, slug });
      return result as CollectionDto;
    },

    /**
     * Delete a collection (hard delete).
     * All `collection_members` rows are removed via ON DELETE CASCADE.
     */
    async deleteCollection(uuid: string): Promise<{ ok: true }> {
      const result = await api.collection.delete({ uuid });
      return result as { ok: true };
    },

    /**
     * List all recipes in a collection.
     * Returns `{ members: CollectionMemberDto[] }` — each member row carries
     * `collection_uuid`, `recipe_uuid`, and `added_at`.
     */
    async listRecipesInCollection(
      collectionUuid: string,
    ): Promise<{ members: CollectionMemberDto[] }> {
      const result = await api.collection.listMembers({ collectionUuid });
      return result as { members: CollectionMemberDto[] };
    },

    /**
     * List all collections a recipe belongs to.
     * Returns `{ members: CollectionMemberDto[] }`.
     */
    async listCollectionsForRecipe(
      recipeUuid: string,
    ): Promise<{ members: CollectionMemberDto[] }> {
      const result = await api.collection.listRecipeMemberships({ recipeUuid });
      return result as { members: CollectionMemberDto[] };
    },

    /**
     * List EVERY membership for the authenticated user in one call (perf plan
     * §2.C). Batched replacement for calling `listRecipesInCollection` once per
     * collection on library mount. Returns `{ members: CollectionMembershipDto[] }`
     * where each row carries `collection_uuid` + `recipe_uuid` (no `added_at`).
     */
    async listAllMemberships(): Promise<{ members: CollectionMembershipDto[] }> {
      const result = await api.collection.listAllMembers({});
      return result as { members: CollectionMembershipDto[] };
    },

    /**
     * Add a recipe to a collection.
     * Idempotent: adding an already-present recipe is a no-op.
     */
    async addMembership(collectionUuid: string, recipeUuid: string): Promise<{ ok: true }> {
      const result = await api.collection.addMember({ collectionUuid, recipeUuid });
      return result as { ok: true };
    },

    /**
     * Remove a recipe from a collection.
     * Returns 404 if the membership does not exist.
     */
    async removeMembership(collectionUuid: string, recipeUuid: string): Promise<{ ok: true }> {
      const result = await api.collection.removeMember({ collectionUuid, recipeUuid });
      return result as { ok: true };
    },
  };
}

export type CollectionsClient = ReturnType<typeof createCollectionsClient>;
